/**
 * Consent panel — preferences modal interactions.
 */

import { trapFocus } from '../atoms/focus-trap';
import type { ConsentMap } from '../types/consent.d';

const CATEGORIES = ['analytics', 'advertising', 'personalization'] as const;

let panelEl: HTMLElement | null = null;
let overlayEl: HTMLElement | null = null;
let previousFocus: Element | null = null;
let currentStyle: 'panel' | 'modal' = 'panel';
let previousBodyOverflow = '';
let isBodyScrollLocked = false;

export function initPanel(panel: HTMLElement | null, overlay: HTMLElement | null, style: 'panel' | 'modal' = 'panel'): void {
  panelEl = panel;
  overlayEl = overlay;
  currentStyle = style;
}

export function openPanel(consent: ConsentMap): void {
  if (!panelEl || !overlayEl) return;

  syncToggles(consent);

  previousFocus = document.activeElement;
  overlayEl.removeAttribute('hidden');
  overlayEl.classList.add('flt-tm-overlay--visible');
  panelEl.removeAttribute('hidden');
  panelEl.classList.add('flt-tm-panel--open');
  if (currentStyle === 'modal') {
    panelEl.classList.add('flt-tm-panel--modal');
  }
  lockBodyScroll();

  const first = panelEl.querySelector<HTMLElement>('button, input, [tabindex]:not([tabindex="-1"])');
  first?.focus();
}

export function closePanel(): void {
  if (!panelEl || !overlayEl) return;

  panelEl.classList.remove('flt-tm-panel--open', 'flt-tm-panel--modal');
  panelEl.setAttribute('hidden', '');
  overlayEl.classList.remove('flt-tm-overlay--visible');
  overlayEl.setAttribute('hidden', '');
  unlockBodyScroll();

  if (previousFocus instanceof HTMLElement) {
    previousFocus.focus();
    previousFocus = null;
  }
}

function syncToggles(consent: ConsentMap): void {
  if (!panelEl) return;
  for (const cat of CATEGORIES) {
    const toggle = panelEl.querySelector<HTMLInputElement>(`[data-flt-tm-toggle="${cat}"]`);
    if (toggle) toggle.checked = !!consent[cat];
  }
}

export function readToggles(): ConsentMap {
  const result: ConsentMap = { necessary: true };
  if (!panelEl) return result;

  for (const cat of CATEGORIES) {
    const toggle = panelEl.querySelector<HTMLInputElement>(`[data-flt-tm-toggle="${cat}"]`);
    result[cat] = toggle ? toggle.checked : false;
  }
  return result;
}

export function isPanelVisible(): boolean {
  return !!panelEl && !panelEl.hasAttribute('hidden');
}

export function handlePanelKeydown(e: KeyboardEvent): void {
  if (!panelEl || panelEl.hasAttribute('hidden')) return;

  if (e.key === 'Tab') {
    trapFocus(panelEl, e);
  }
}

function lockBodyScroll(): void {
  if (isBodyScrollLocked) return;

  previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  isBodyScrollLocked = true;
}

function unlockBodyScroll(): void {
  if (!isBodyScrollLocked) return;

  document.body.style.overflow = previousBodyOverflow;
  previousBodyOverflow = '';
  isBodyScrollLocked = false;
}
